import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Typography } from 'ui-kit/Typography';
import { ToggleButton, ToggleButtonChangeEvent } from 'primereact/togglebutton';
import {FormattedMessage} from "react-intl";
import Trash from "assets/trash.svg";

interface ArtistItemProps {
  label: string;
  checked: boolean;
  onChange: (event: ToggleButtonChangeEvent) => void;
  onRemove: () => void;
  name?: string;
  role?: string
}

export const ArtistItem: React.FC<ArtistItemProps> = props => {

  const theme = useTheme();
  const fullName = `${props.name ?? ''}${props.name && props.label ? ' ' : ''}${props.label ?? ''}`.trim();
  const displayName = fullName.length > 12 ? `${fullName.slice(0, 12)}...` : fullName;
  // `checked` is the single source of truth for UI state (parent decides who is Primary).
  const isPrimary = !!props.checked;

  return (
    <Container>
      <TopRow>
        <NameText title={fullName}>
          <Typography variant="body6" color={theme.palette.text.link}>
            {displayName}
          </Typography>
        </NameText>
        <TrashContainer
          role="button"
          aria-label="Remove artist"
          onClick={(e) => {
            e.stopPropagation();
            props.onRemove();
          }}
        >
          <Trash />
        </TrashContainer>
      </TopRow>
      <Main>
        <Box>
          <StyledToggleButton checked={isPrimary} onChange={props.onChange} />
          <span style={{ color: isPrimary ? '#000000' : '#00000080' }}>
            {!isPrimary ? (
              <FormattedMessage
                id="NewRelease.ReleaseInfo.Featuring"
                defaultMessage="Featuring"
              />
            ) : (
              <FormattedMessage
                id="NewRelease.ReleaseInfo.Primary"
                defaultMessage="Primary"
              />
            )}
          </span>
        </Box>
      </Main>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 5px;
  border: 1px solid #A4A4A4;
  border-radius: 7px;
  padding: 4px 6px 8px 9px;
  width: 160px;
  height: 50px;
`
const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`
const NameText = styled.div`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const TrashContainer = styled.button`
  border: none;
  background: transparent;
  padding: 2px;
  margin: -2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    pointer-events: none;
  }
`;
const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`
const StyledToggleButton = styled(ToggleButton)`
  width: 26px;
  height: 14px;
  border-radius: 30px;
  background-color: #ccc;
  position: relative;
  transition: background-color 0.3s;
  border: none;

  .p-button {
    display: none;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 10px;
    height: 10px;
    background-color: white;
    border-radius: 50%;
    transition: transform 0.3s;
  }

  &:has(input[value="true"]) {
    background-color: ${({ theme }) => theme.palette.button.primary};
  }

  &:has(input[value="true"])::before {
    transform: translateX(12px);
  }
`
const Box = styled.div`
  display:flex;
  align-items: center;
  gap:4px;

  span{
    font-family: SF Pro Display;
    font-weight: 400;
    font-style: Regular;
    font-size: 13px;
    leading-trim: NONE;
    line-height: 15px;
    letter-spacing: 0;
    vertical-align: middle;
  }
`
